import { AnalyticEvent } from ':fat/analytics/events';
import { logError } from ':fat/analytics/logActions';
import { Database } from ':fat/database/Database';
import { errorToString } from ':fat/helpers/errors';
import { reportError } from ':fat/init/bugsnag';

import { sanityCheck } from './sanityCheck';

export type HandlerContext = object; // nothing yet

let database: Database;

export async function handleMessage(
  _context: HandlerContext,
  message: TaggedMessage<'databaseWorker'>,
) {
  switch (message.kind) {
    case 'PING': {
      void MessageBus.reply(message, { kind: 'PONG' });
      break;
    }
    case 'SETUP': {
      MessageBus.registerWorkerPort('blockchainWorkerPort', message.blockchainWorkerPort);
      database = new Database('fat.db');
      await database.ready();
      void MessageBus.reply(message, { kind: 'READY' });
      break;
    }
    case 'RESET': {
      await database.reset();
      break;
    }
    case 'CLEAR_ALL_DATA': {
      await database.clearAllTables();
      void MessageBus.reply(message, { kind: 'READY' });
      break;
    }
    case 'CLEAR_ADDRESS_DATA': {
      await database.clearAddressTables();
      void MessageBus.reply(message, { kind: 'READY' });
      break;
    }
    case 'SANITY_CHECK': {
      const { success, errorMessage } = await sanityCheck();
      void MessageBus.reply(message, {
        kind: 'SANITY_CHECK_RESPONSE',
        success,
        error: errorMessage,
      });
      break;
    }
    case 'QUERY': {
      try {
        if (!database) {
          throw new Error('Database not initialized yet');
        }

        const result = database.exec(message.query);
        void MessageBus.reply(message, { kind: 'QUERY_RESULT', result });
      } catch (error: unknown) {
        logError(AnalyticEvent.IndexedDbQueryError, {
          message: errorToString(error),
          query: message.query.sql,
          queryParams: message.query.parameters,
        });
        reportError(error as Error, {
          context: 'queryHandler',
          metadata: [
            {
              name: 'details',
              data: { query: message.query.sql, error: errorToString(error) },
            },
          ],
        });
        void MessageBus.reply(
          message,
          { kind: 'QUERY_ERROR', error: errorToString(error) },
          { rejection: true },
        );
      }
      break;
    }

    // These messages are replies that are handled by the sender of the
    // original message as an awaited Promise. Nothing to do here, other than
    // convince TypeScript that we've handled all message kinds.
    case 'PONG': {
      break;
    }
    default:
      // @ts-expect-error to guarantee we've handled all message kinds. If
      // this is broken, we missed a possible message. Hover over `message.kind`
      // below to see what was missed.
      throw new Error(`Unexpected message from main page: ${message.kind}`);
  }
}
